import React from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import ContactForm from "@/components/ContactForm";
import InvestmentDisclaimer from "@/components/InvestmentDisclaimer";

const reasons = [
  { title: "Governance-Led Structure", body: "Sponsor-grade governance applied from origination through to long-term stewardship." },
  { title: "Strategic Development Platforms", body: "Investments aligned with national priorities, not opportunistic transactions." },
  { title: "Strong Stakeholder Alignment", body: "Government, communities and capital partners aligned around shared outcomes." },
  { title: "Scalable Opportunities", body: "Platforms architected to compound across capital deployments." },
  { title: "Long-Term Value Creation", body: "Performance measured over decades, with disciplined capital allocation." },
];

const opportunities = [
  {
    title: "Bambereaux Integrated Development Platform",
    meta: "Saint Vincent & the Grenadines · Residential, Hospitality, Healthcare",
    metric: "US$158M+ projected platform value",
    to: "/bambereaux",
    testId: "ir-opp-bambereaux",
  },
  {
    title: "National Agro-Industrial Import Substitution Platform",
    meta: "Saint Vincent & the Grenadines · Food Sovereignty, Processing, Export",
    metric: "120 acres · 6,000 tonnes capacity · CARICOM export",
    to: "/agro-industrial-platform",
    testId: "ir-opp-agro",
  },
];

const audiences = [
  "Family Offices",
  "Institutional Investors",
  "Development Finance Institutions",
  "Strategic Partners",
  "Impact Investors",
  "Government Stakeholders",
];

export default function InvestorRelations() {
  return (
    <>
      <PageHeader
        testId="ir-header"
        eyebrow="Investor Relations"
        title="A measured conversation with institutional capital."
        intro="Evergreen engages with family offices, institutional investors, development finance institutions and strategic partners aligned with patient, governance-led capital deployment."
        image="https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/2pownbf7_IMG_0624.jpeg"
        overlayTone="strong"
      />

      {/* Why Invest */}
      <section data-testid="ir-why-invest" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-16">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline mb-8">Why Invest With Evergreen</div>
              <h2 className="evg-display-lg">Five reasons institutional capital partners with us.</h2>
            </div>
            <div className="lg:col-span-8">
              {reasons.map((r, i) => (
                <div
                  key={r.title}
                  data-testid={`ir-reason-${i + 1}`}
                  data-reveal
                  data-reveal-delay={String((i % 3) * 100)}
                  className="py-10 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
                >
                  <span className="col-span-12 md:col-span-1 evg-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl leading-tight">{r.title}</h3>
                  <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-[1.8] font-light">
                    {r.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Current Opportunities — editorial rows, no boxed cards */}
      <section data-testid="ir-opportunities" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Current Opportunities</div>
            <h2 className="evg-display-lg">Two flagship platforms — actively engaging investors.</h2>
          </div>

          <div>
            {opportunities.map((o, i) => (
              <Link
                key={o.title}
                to={o.to}
                data-testid={o.testId}
                data-reveal
                data-reveal-delay={String(i * 120)}
                className="group block py-12 md:py-16 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-6 items-baseline">
                  <div className="lg:col-span-1 evg-overline-gold">
                    0{i + 1}
                  </div>
                  <div className="lg:col-span-7">
                    <h3 className="evg-display-md group-hover:text-[#D4AF37] transition-colors">
                      {o.title}
                    </h3>
                    <p className="evg-meta mt-4">{o.meta}</p>
                  </div>
                  <div className="lg:col-span-4 lg:text-right">
                    <p className="text-[15px] text-[#0A1F44]/75 font-light leading-relaxed">
                      {o.metric}
                    </p>
                    <span className="mt-5 inline-block text-[11px] uppercase tracking-[0.24em] text-[#0A1F44] group-hover:text-[#D4AF37] border-b border-[#D4AF37] pb-1.5">
                      Explore Platform →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section data-testid="ir-audiences" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline mb-8">Investor Engagement</div>
              <h2 className="evg-display-lg">Who we partner with.</h2>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
                {audiences.map((a, i) => (
                  <div
                    key={a}
                    data-reveal
                    data-reveal-delay={String((i % 3) * 100)}
                    className="py-6 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 sm:[&:nth-last-child(-n+2)]:border-b flex items-baseline gap-5"
                  >
                    <span className="evg-index">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[16px] text-[#0A1F44] font-light">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section data-testid="ir-contact" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">Engage</div>
              <h2 className="evg-display-lg">Begin a conversation with our investor relations team.</h2>
              <p className="evg-lead mt-8 max-w-md">
                Share a brief overview of your interest and a member of our team will respond
                personally within two business days.
              </p>
              <p className="mt-8 text-[14px] text-[#0A1F44]/65 font-light">
                Alternatively, email{" "}
                <a
                  href="mailto:investors@evergreenglobalholdings.com"
                  className="text-[#0A1F44] border-b border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                  data-testid="ir-email-link"
                >
                  investors@evergreenglobalholdings.com
                </a>
                .
              </p>
            </div>
            <div className="lg:col-span-7" data-reveal data-reveal-delay="200">
              <ContactForm sourcePage="investor-relations" testIdPrefix="ir" />
            </div>
          </div>
          <div className="mt-24 pt-12 border-t border-[#0A1F44]/12">
            <InvestmentDisclaimer />
          </div>
        </div>
      </section>
    </>
  );
}
